import {
  createAction,
  createAsyncThunk,
  createSlice,
  current,
} from "@reduxjs/toolkit";
import { CART_FROM, CART_STATUS } from "../constants/status";
import { ICartInfo, ICartPosition } from "../interface/ICart";
import {
  CancelSOServiceParamModel,
  DeleteParamModel,
  DeleteServiceParamModel,
  GetCartServiceParamModel,
  PlaceCartParamModel,
  ValidateServiceParamModel,
} from "../model/cart";

import CartService from "../service/CartService";
import { RootState } from "../store/store";

import { logoutUser } from "./common";

const _service = new CartService();

export const resetCart = createAction("cart/reset");

type SliceState = {
  value: ICartInfo;
  status: string;
  cartFromLocation: string;
  errormessages?: string[];
  snapshotRunningError?: boolean;
  apiCallCount: number;
  hasError?: boolean;
  maxCapacityError?: any;
  reason?:any;
};
type ActionReturn = {
  after?: ICartPosition[];
  before?: ICartPosition[];
  errors?: string[];
  hasError: boolean;
  id: number;
  snapshotRunningError: boolean;
};

const initialState: SliceState = {
  value: {},
  status: CART_STATUS.INITIAL_CART,
  cartFromLocation: CART_FROM.INITIAL,
  apiCallCount: 0,
} as SliceState;

export const getCartAsync = createAsyncThunk(
  "cart/getCart",
  async (params: GetCartServiceParamModel) => {
    const response = await _service.getCartService(params);
    return response;
  }
);

export const placeCartAsycn = createAsyncThunk(
  "cart/placeCart",
  async (params: PlaceCartParamModel) => {
    const response = await _service.placeCartService(params);
    return response;
  }
);

export const deleteCartAsycn = createAsyncThunk(
  "cart/deleteCart",
  async (params: DeleteServiceParamModel) => {
    const response = await _service.deleteCartService(params);

    return response;
  }
);

export const cancelSOAsycn = createAsyncThunk(
  "cart/cancelSO",
  async (params: CancelSOServiceParamModel) => {
    const response = await _service.cancelSOService(params);

    return response;
  }
);




export const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    createCart: (state, action) => {
      state.value = action.payload;
      state.status = CART_STATUS.CREATE_CART;
    },
    updateCart: (state, action) => {
      state.value = action.payload;
    },
    addPositionToCart: (state, action) => {
      const _valueFromLocal = action.payload.value ? action.payload.value : 0;
      const cartFound = state.value.positions?.find(
        (item) => item.itemId === action.payload.id
      );
      if (cartFound) {
        if (!cartFound.value) cartFound.value = 0;
        cartFound.value = _valueFromLocal;
        cartFound.isSample = action.payload.isSample
          ? action.payload.isSample
          : false;
        cartFound.updatedValue = true;
        cartFound.itemNote = action.payload.itemNote;
        cartFound.canOrder=action.payload.canOrder;
      } else {
        state.value.positions?.push({
          itemId: action.payload.id,
          standingOrderValue: 0,
          value: _valueFromLocal,
          isSample: action.payload.isSample ? action.payload.isSample : false,
          itemNote: action.payload.itemNote,
          updatedValue: true,
          canOrder:action.payload.canOrder,
        });
      }
    },

    deletePositionFromCart: (state: SliceState, action) => {
      const filter = state.value.positions?.filter(
        (v: ICartPosition) => v.itemId !== action.payload
      );
      state.value.positions = filter;
    },
    updateCartFromLocation: (state: SliceState, action) => {
      state.cartFromLocation = action.payload;
    },
    updateCartStatus: (state: SliceState, action) => {
      state.status = action.payload;
    },
    updateCartReason: (state, action) => {
      state.value.reason = action.payload;
    },
    resetMaxCapacityErrorDetails: (state: SliceState) => {
      state.hasError = false;
      state.maxCapacityError = null;
    },
    resetNewCartOnErrorSnapshot: (state: SliceState) => {
      state.hasError = false;
      state.maxCapacityError = null;
      state.status = CART_STATUS.INITIAL_CART;
      state.value.positions = [];
    },
  },
  extraReducers: (builder) => {
    builder
      //GET
      .addCase(getCartAsync.pending, (state) => {
        state.status = CART_STATUS.LOADING_GET_CART;
      })
      .addCase(getCartAsync.fulfilled, (state, action) => {
        const posFromCart = state.value.positions
          ? [...state.value.positions]
          : [];
        state.value = action.payload;
        state.status = CART_STATUS.LOADED_GET_CART;
        state.value.positionsFromCart = posFromCart;
        state.value.positionsFromServer = [...action.payload.positions];
      })
      .addCase(getCartAsync.rejected, (state) => {
        state.status = CART_STATUS.FAILED_GET_CART;
      })
      //PLACE
      .addCase(placeCartAsycn.pending, (state) => {
        state.status = CART_STATUS.SEND_PLACE_CART;
      })
      .addCase(placeCartAsycn.fulfilled, (state: SliceState, action: any) => {
        if (action.payload.hasError) {
          state.status = CART_STATUS.FAILED_PLACE_CART;
          state.errormessages = action.payload.errors;
          state.snapshotRunningError = action.payload.snapshotRunningError;
          if (action.payload.maxCapacityError) {
            state.maxCapacityError = {
              maxCapacityError: action.payload.maxCapacityError,
              hasError: action.payload.hasError,
              itemsOverMaxCapacities: action.payload.itemsOverMaxCapacities,
            };
          }
        } else {
          state.value.id = action.payload.id;
          state.status = CART_STATUS.DONE_PLACE_CART;
        }
      })
      .addCase(placeCartAsycn.rejected, (state) => {
        state.status = CART_STATUS.FAILED_PLACE_CART;
      })
      //DELETE
      .addCase(deleteCartAsycn.pending, (state) => {
        state.status = CART_STATUS.SEND_DELETE_CART;
      })
      .addCase(deleteCartAsycn.fulfilled, (state: SliceState) => {
        state.status = CART_STATUS.DONE_DELETE_CART;
        state.cartFromLocation = CART_FROM.INITIAL;
      })
      .addCase(deleteCartAsycn.rejected, (state) => {
        state.status = CART_STATUS.FAILED_DELETE_CART;
      })
      //CANCEL SO
      .addCase(cancelSOAsycn.pending, (state) => {
        state.status = CART_STATUS.SEND_DELETE_CART;
      })
      .addCase(cancelSOAsycn.fulfilled, (state: SliceState) => {
        state.status = CART_STATUS.DONE_CANCELSO_CART;
        state.cartFromLocation = CART_FROM.INITIAL;
      })
      .addCase(cancelSOAsycn.rejected, (state) => {
        state.status = CART_STATUS.FAILED_DELETE_CART;
      })

      .addCase(logoutUser, () => {
        return initialState;
      })
      .addCase(resetCart, () => {
        return initialState;
      })
  },
});

export const {
  createCart,
  updateCart,
  addPositionToCart,
  deletePositionFromCart,
  updateCartFromLocation,
  updateCartStatus,
  resetMaxCapacityErrorDetails,
  resetNewCartOnErrorSnapshot,
  updateCartReason
} = cartSlice.actions;
export const getCart = (state: RootState) => state.cart.value;
export const hasCart = (state: RootState) =>
  state.cart.value &&
  state.cart.value.addressId &&
  (state.cart.status !== CART_STATUS.INITIAL_CART ||
    state.cart.status !== CART_STATUS.LOADING_GET_CART);
export const getCartItemsCount = (state: RootState) =>
  state.cart.value?.positions ? state.cart.value.positions.length : 0;
export const getCartValueStatus = (state: RootState) =>
  state.cart.value ? state.cart.value.status : 0;
export const getCartFromLocation = (state: RootState) =>
  state.cart.cartFromLocation;
export const getCartItems = (state: RootState) => state.cart.value?.positions;
export const getCartStatus = (state: RootState) => state.cart.status;
export const getCartErrors = (state: RootState) => state.cart.errormessages;
export const getCartIfSnapShotError = (state: RootState) =>
  state.cart.snapshotRunningError;
export const getMaxCapacityError = (state: RootState) =>
  state.cart.maxCapacityError && state.cart.maxCapacityError.maxCapacityError
    ? state.cart.maxCapacityError
    : null;

export const getCartDateReson = (state: RootState) => state.cart.reason;
export default cartSlice.reducer;
